import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/AuthContext';
import { PetAvatar } from '@/components/PetAvatar';
import { TappableCard } from '@/utils/animations';
import { DoodleStar, DoodleNote } from '@/components/Doodles';
import DreamDiary from '@/components/DreamDiary';

// Deterministic thumbnail color from topic string
const THUMB_COLORS = ['#F0E6D2','#E8D4F1','#D4F1E8','#F1E8D4','#D4E8F1','#F1D4E8'];
const thumbColor = (str) => THUMB_COLORS[(str?.charCodeAt(0) || 0) % THUMB_COLORS.length];

// Parse visual_url JSON safely and return first slide
const firstSlide = (visualUrl) => {
  if (!visualUrl) return null;
  try {
    const slides = JSON.parse(visualUrl);
    return Array.isArray(slides) && slides.length ? slides[0] : null;
  } catch {
    return null;
  }
};

// Relative time helper
const timeAgo = (isoStr) => {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Match a story's content_preview to a mastery topic
const findMastery = (storyPreview, masteryTopics) => {
  if (!storyPreview || !masteryTopics?.length) return null;
  const norm = storyPreview.slice(0, 80).toLowerCase().trim();
  return masteryTopics.find(t => {
    const m = (t.content_preview || '').slice(0, 80).toLowerCase().trim();
    return m === norm || m.startsWith(norm.slice(0, 40)) || norm.startsWith(m.slice(0, 40));
  }) || null;
};

const BAR_COLOR = (pct) => {
  if (pct >= 75) return '#66BB6A';
  if (pct >= 50) return '#448AFF';
  if (pct >= 25) return '#FFD740';
  return 'rgba(0,0,0,0.1)';
};

const MasteryOverview = ({ masteryTopics }) => {
  const mastered  = masteryTopics.filter(t => t.mastery_pct >= 75).length;
  const learning  = masteryTopics.filter(t => t.mastery_pct >= 25 && t.mastery_pct < 75).length;
  const needsWork = masteryTopics.filter(t => t.mastery_pct < 25).length;

  if (!masteryTopics.length) return null;

  return (
    <div style={{
      display: 'flex', gap: 8, marginBottom: 16,
    }}>
      {[
        { label: 'Mastered', count: mastered,  color: '#2E7D32', bg: 'rgba(102,187,106,0.08)',   border: 'rgba(102,187,106,0.15)'   },
        { label: 'Learning', count: learning,  color: '#FFD740', bg: 'rgba(255,215,64,0.08)', border: 'rgba(255,215,64,0.15)' },
        { label: 'Needs work', count: needsWork, color: '#448AFF', bg: 'rgba(68,138,255,0.08)', border: 'rgba(68,138,255,0.15)' },
      ].map(({ label, count, color, bg, border }) => (
        <div key={label} style={{
          flex: 1, background: bg,
          border: `1.5px dashed ${border}`,
          borderRadius: 4, padding: '12px 8px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 24, fontWeight: 500, color, lineHeight: 1 }}>{count}</div>
          <div style={{ fontSize: 10, color: '#666666', marginTop: 4, fontWeight: 500, letterSpacing: 0.3 }}>
            {label.toUpperCase()}
          </div>
        </div>
      ))}
    </div>
  );
};

const StoryCard = ({ story, mastery, onPlay, index }) => {
  const slide   = firstSlide(story.visual_url);
  const bg      = slide?.color || thumbColor(story.content_preview);
  const emoji   = slide?.emoji || '📖';
  const title   = (story.content_preview || 'Story').slice(0, 50);
  const mastered = mastery?.mastery_pct >= 75;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <TappableCard
        onClick={() => onPlay(story.content_preview)}
        aria-label={`Open story: ${title}`}
        style={{
          background: '#FFFFFF',
          border: mastered
            ? '1.5px dashed rgba(102,187,106,0.4)'
            : '1.5px dashed #CCCCCC',
          borderLeft: mastered ? '3px solid #66BB6A' : '3px solid #448AFF',
          borderRadius: 4,
          padding: 12,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
        }}
      >
        {/* Thumbnail */}
        <div style={{
          width: 56, height: 56, borderRadius: 4, flexShrink: 0,
          background: bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26,
          border: '1px solid rgba(0,0,0,0.04)',
        }}>
          {emoji}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
            <p style={{
              fontSize: 16, fontWeight: 500, color: '#1A1A2E',
              margin: '0 0 2px 0',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flex: 1,
            }}>
              {title}{title.length >= 50 ? '…' : ''}
            </p>
            {mastered && (
              <span style={{ fontSize: 14, color: '#2E7D32', flexShrink: 0, fontWeight: 500 }}>✅</span>
            )}
          </div>

          <p style={{ fontSize: 11, color: '#666666', margin: '0 0 6px 0' }}>
            {timeAgo(story.created_at)}
            {story.language ? ` · ${story.language.toUpperCase()}` : ''}
          </p>

          {mastery ? (
            <>
              {/* Stars */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <div>
                  {Array.from({ length: mastery.total_questions || 4 }, (_, i) => (
                    <span key={i} style={{ fontSize: 11, opacity: i < mastery.best_score ? 1 : 0.2 }}>⭐</span>
                  ))}
                </div>
                <span style={{ fontSize: 12, color: BAR_COLOR(mastery.mastery_pct), fontWeight: 500 }}>
                  {mastery.mastery_pct}%
                </span>
              </div>
              {/* Mastery bar */}
              <div style={{ height: 3, borderRadius: 2, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${mastery.mastery_pct}%` }}
                  transition={{ duration: 0.6, delay: index * 0.05 + 0.2, ease: 'easeOut' }}
                  style={{ height: '100%', borderRadius: 2, background: BAR_COLOR(mastery.mastery_pct) }}
                />
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: '#888888', fontStyle: 'italic' }}>
              Take the quiz to track mastery
            </div>
          )}
        </div>

        {/* Play button */}
        <button
          onClick={(e) => { e.stopPropagation(); onPlay(story.content_preview); }}
          aria-label={`Play story: ${title}`}
          style={{
            width: 48, height: 48, flexShrink: 0,
            background: 'rgba(68,138,255,0.1)',
            border: '1.5px dashed rgba(68,138,255,0.35)',
            borderRadius: 8, color: '#448AFF', fontSize: 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 500,
          }}
        >
          ▶
        </button>
      </TappableCard>
    </motion.div>
  );
};

export default function StoryLibrary({ user, masteryTopics = [], onGenerateStory }) {
  const { getAccessToken } = useAuth();
  const petType = localStorage.getItem('you_pet_type') || 'panda';

  const [stories, setStories]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const timeout = setTimeout(() => setLoading(false), 5000);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    const token = getAccessToken() || '';
    fetch(`${BACKEND}/api/my-stories`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setStories(data.stories || []))
      .catch(() => setStories([]))
      .finally(() => { clearTimeout(timeout); setLoading(false); });
  }, [user]);

  const handlePlay = (topic) => {
    if (topic) onGenerateStory(topic);
  };

  // Loading skeleton
  if (loading) {
    return (
      <div style={{ padding: '16px 16px 140px', maxWidth: 720, margin: '0 auto' }}>
        {[1,2,3].map(i => (
          <div key={i} style={{
            height: 80, borderRadius: 4, marginBottom: 8,
            background: 'rgba(0,0,0,0.04)',
            animation: 'petIdle 2.5s ease-in-out infinite',
          }} />
        ))}
      </div>
    );
  }

  // Not signed in — show local story history if any, else prompt to generate
  if (!user) {
    const localHistory = (() => {
      try { return JSON.parse(localStorage.getItem('you_story_history') || '[]'); }
      catch { return []; }
    })();

    if (localHistory.length === 0) {
      return (
        <div style={{
          padding: '60px 24px 80px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 4 }}>📚</div>
          <p style={{ fontSize: 18, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', margin: 0 }}>No stories yet!</p>
          <p style={{ fontSize: 16, color: '#555555', margin: 0, lineHeight: 1.5 }}>
            Generate your first audio story from the Home tab
          </p>
          <button
            onClick={() => onGenerateStory('')}
            style={{
              marginTop: 8, padding: '14px 28px', borderRadius: 12, border: 'none',
              background: '#448AFF',
              color: '#fff', fontSize: 16, fontWeight: 500, fontFamily: "'Patrick Hand', cursive", cursor: 'pointer',
            }}
          >
            ✨ Generate a story
          </button>
        </div>
      );
    }

    return (
      <div style={{ padding: '16px 16px 140px', maxWidth: 720, margin: '0 auto', width: '100%' }}>
        <p style={{ fontSize: 26, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', margin: '0 0 16px 0' }}>
          Your Stories
        </p>
        {localHistory.map((story, i) => (
          <div
            key={story.id || i}
            onClick={() => story.topic && onGenerateStory(story.topic)}
            style={{
              background: '#F8F8F8',
              borderRadius: 4, padding: 16, marginBottom: 8,
              border: '1.5px dashed #CCCCCC',
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', cursor: story.topic ? 'pointer' : 'default',
            }}
          >
            <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
              <p style={{
                fontSize: 16, fontWeight: 500, color: '#1A1A2E',
                margin: '0 0 4px 0',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {story.topic || 'Story'}
              </p>
              <p style={{ fontSize: 14, color: '#555555', margin: 0 }}>
                {new Date(story.timestamp).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
                {story.score !== null ? ` · Score: ${story.score}/3` : ''}
              </p>
            </div>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(68,138,255,0.1)',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 16,
            }}>
              🎧
            </div>
          </div>
        ))}
        <button
          onClick={() => onGenerateStory('')}
          style={{
            width: '100%', marginTop: 8, padding: '14px 0', borderRadius: 14,
            border: 'none', background: '#448AFF',
            color: '#fff', fontSize: 16, fontWeight: 500, fontFamily: "'Patrick Hand', cursive", cursor: 'pointer',
          }}
        >
          ✨ Generate new story
        </button>
      </div>
    );
  }

  // Empty state
  if (!stories.length) {
    return (
      <div style={{
        padding: '60px 24px 80px', textAlign: 'center',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      }}>
        <PetAvatar size={80} state="idle" petType={petType} />
        <p style={{ fontSize: 18, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', margin: 0 }}>No stories yet!</p>
        <p style={{ fontSize: 15, color: '#555555', margin: 0, lineHeight: 1.5 }}>
          Generate your first story from the home screen.
        </p>
        <button
          onClick={() => onGenerateStory('')}
          style={{
            marginTop: 8, padding: '14px 28px', borderRadius: 12, border: 'none',
            background: '#448AFF',
            color: '#fff', fontSize: 16, fontWeight: 500, fontFamily: "'Patrick Hand', cursive", cursor: 'pointer',
          }}
        >
          ✨ Generate a story
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 16px 140px', maxWidth: 720, margin: '0 auto', width: '100%', position: 'relative' }}>
      {/* Doodle decorations */}
      <div style={{ position: 'absolute', top: 12, right: 12, pointerEvents: 'none' }}>
        <DoodleStar color="#FFD740" opacity={0.2} size={14} />
      </div>
      <div style={{ position: 'absolute', bottom: 100, left: 8, pointerEvents: 'none' }}>
        <DoodleNote color="#448AFF" opacity={0.15} size={12} />
      </div>

      {/* Dream Diary */}
      <DreamDiary user={user} />
      <div style={{ borderTop: '1.5px dashed #DDDDDD', margin: '0 0 16px' }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 18, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', margin: 0 }}>
          📚 My Library
        </p>
        <span style={{ fontSize: 11, color: '#666666', fontWeight: 500 }}>
          {stories.length} stor{stories.length === 1 ? 'y' : 'ies'}
        </span>
      </div>

      {/* Mastery overview */}
      <MasteryOverview masteryTopics={masteryTopics} />

      {/* Story cards */}
      {stories.map((story, i) => (
        <StoryCard
          key={story.id}
          story={story}
          mastery={findMastery(story.content_preview, masteryTopics)}
          onPlay={handlePlay}
          index={i}
        />
      ))}

      {/* Generate new story button */}
      <button
        onClick={() => onGenerateStory('')}
        style={{
          width: '100%', marginTop: 8, padding: '14px 28px', borderRadius: 14,
          border: 'none', background: '#448AFF',
          color: '#fff', fontSize: 16, fontWeight: 500, fontFamily: "'Patrick Hand', cursive", cursor: 'pointer',
        }}
      >
        ✨ Generate new story
      </button>
    </div>
  );
}
